import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ajax from '../utils/ajaxHelper';

const useAmenities = () => {
  const params = useParams();
  const [amenities, setAmenities] = useState([]);
  const [amenityFilters, setAmenityFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    limit: 12,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    amenityTypes: [],
    amenityCategories: [],
    featuredOnly: false,
  });

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  const fetchAmenities = useCallback(
    async (communityId, page = 1, customFilters = null, skipLoadingState = false) => {
      if (!communityId) return;

      if (!skipLoadingState) {
        setLoading(true);
      }
      setError(null);

      try {
        const requestData = {
          communityId,
          page,
          limit: pagination.limit,
          ...(customFilters || filters),
        };

        const response = await ajax.post('community/get-amenities-paginated', requestData, {
          networkParams: { showLoader: false },
        });

        if (response.status) {
          setAmenities(response.amenitiesResult || []);
          setPagination(response.pagination || {});
        } else {
          setError('Failed to fetch amenities');
          setAmenities([]);
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching amenities');
        setAmenities([]);
      } finally {
        if (!skipLoadingState) {
          setLoading(false);
        }
      }
    },
    [filters, pagination.limit],
  );

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      amenityTypes: [],
      amenityCategories: [],
      featuredOnly: false,
    });
  }, []);

  const goToPage = useCallback(
    (page) => {
      if (params?.id) {
        fetchAmenities(params.id, page);
      }
    },
    [params, fetchAmenities],
  );

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      goToPage(pagination.currentPage + 1);
    }
  }, [pagination.hasNextPage, pagination.currentPage, goToPage]);

  const prevPage = useCallback(() => {
    if (pagination.hasPrevPage) {
      goToPage(pagination.currentPage - 1);
    }
  }, [pagination.hasPrevPage, pagination.currentPage, goToPage]);

  const refreshAmenities = useCallback(() => {
    if (params?.id) {
      fetchAmenities(params.id, pagination.currentPage);
    }
  }, [params, pagination.currentPage, fetchAmenities]);

  // Extract filter dependencies to avoid complex expressions in dependency array
  const searchFilter = filters.search;
  const amenityTypesFilter = filters.amenityTypes;
  const amenityCategoriesFilter = filters.amenityCategories;
  const featuredOnlyFilter = filters.featuredOnly;
  const communityId = params?.id;

  // Auto-fetch when filters change
  // NOTE: Debouncing is handled in the component layer (AmenitiesAll.js)
  // This effect only triggers when filters are actually updated
  useEffect(() => {
    if (!communityId) return;

    // On initial mount, fetch immediately
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchAmenities(communityId, 1);
      return;
    }

    // Fetch with updated filters (debouncing already handled by component)
    fetchAmenities(communityId, 1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter, amenityTypesFilter, amenityCategoriesFilter, featuredOnlyFilter, communityId]);

  // Get amenity categories for filtering
  const getAmenityCategories = useCallback(async () => {
    try {
      const response = await ajax.post(
        'community/get-amenity-categories',
        {},
        {
          networkParams: { showLoader: false },
        },
      );
      return response.amenityCategoriesResult || [];
    } catch (err) {
      console.error('Error fetching amenity categories:', err);
      return [];
    }
  }, []);

  // Get amenity types for filtering
  const getAmenityTypes = useCallback(async (amenityTypeName = null) => {
    try {
      const requestData = amenityTypeName ? { amenityTypeName } : {};
      const response = await ajax.post('community/get-amenity-type', requestData, {
        networkParams: { showLoader: false },
      });
      return response.amenitiesResult || [];
    } catch (err) {
      console.error('Error fetching amenity types:', err);
      return [];
    }
  }, []);

  // Get amenity filter counts for filtering UI
  const getAmenityFilters = useCallback(
    async (targetCommunityId = null, featuredOnly = false) => {
      try {
        const resolvedCommunityId = targetCommunityId || communityId;
        if (!resolvedCommunityId) {
          setAmenityFilters([]);
          return [];
        }

        const response = await ajax.post(
          'community/get-amenity-filters',
          {
            communityId: resolvedCommunityId,
            featuredOnly,
          },
          {
            networkParams: { showLoader: false },
          },
        );

        const filtersResult = response?.categoryFilters || [];
        setAmenityFilters(filtersResult);
        return filtersResult;
      } catch (err) {
        console.error('Error fetching amenity filters:', err);
        setAmenityFilters([]);
        return [];
      }
    },
    [communityId],
  );

  useEffect(() => {
    if (!communityId) return;
    getAmenityFilters(communityId, featuredOnlyFilter);
  }, [communityId, featuredOnlyFilter, getAmenityFilters]);

  return {
    // Data
    amenities,
    amenityFilters,
    loading,
    error,
    pagination,
    filters,

    // Actions
    fetchAmenities,
    updateFilters,
    clearFilters,
    goToPage,
    nextPage,
    prevPage,
    refreshAmenities,

    // Helper functions
    getAmenityCategories,
    getAmenityTypes,
    getAmenityFilters,
  };
};

export default useAmenities;
